import { useState } from "react";
import heroImage from "@/assets/hero-barcelona.png.asset.json";
import reelPreview from "@/assets/reel-preview.mp4.asset.json";
import reelFull from "@/assets/reel-full.mp4.asset.json";
import reelPoster from "@/assets/reel-poster.jpg.asset.json";
import { useTransformForm } from "@/components/onboarding/TransformFormContext";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

const PixelArrow = () => (
  <svg viewBox="0 0 12 12" className="h-4 w-4" fill="currentColor" shapeRendering="crispEdges">
    <rect x="1" y="5" width="7" height="2" />
    <rect x="7" y="3" width="2" height="1" />
    <rect x="7" y="8" width="2" height="1" />
    <rect x="9" y="5" width="2" height="2" />
  </svg>
);

const HeroSection = () => {
  const { open: openForm } = useTransformForm();
  const { t } = useTranslation();
  const [reelOpen, setReelOpen] = useState(false);

  return (
    <section className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden bg-background pt-24 pb-12 md:pt-28 md:pb-14">
      {/* Background image */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          src={heroImage.url}
          alt={t("sHome.heroSection.imageAlt")}
          className="w-full h-full object-cover object-top"
          fetchPriority="high"
          decoding="async"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/25"
        />
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 md:px-6 relative">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
          <div className="flex-1">
            {/* Visual claim (not a heading); the page's only H1 is the subheadline below */}
            <p className="max-w-3xl font-serif text-[2.1rem] sm:text-4xl md:text-5xl lg:text-6xl font-normal leading-[1.05] tracking-[-0.01em] text-foreground">
              {t("sHome.heroSection.headline")}
            </p>
            <h1 className="mt-4 max-w-2xl text-base sm:text-lg md:text-xl font-normal leading-snug text-foreground/85">
              {t("sHome.heroSection.subheadline")}
            </h1>

            <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 max-w-xl">
              <button
                type="button"
                onClick={openForm}
                className="inline-flex w-full sm:w-auto items-center justify-between sm:justify-start gap-3 rounded-none bg-foreground text-background pl-5 pr-1.5 py-1.5 md:pl-6 text-base md:text-lg font-normal transition-colors hover:bg-brand-blue hover:text-foreground"
              >
                {t("sHome.heroSection.ctaPrimary")}
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-none bg-background text-foreground">
                  <PixelArrow />
                </span>
              </button>

              <a
                href="#verticales"
                className="inline-flex w-full sm:w-auto items-center justify-between sm:justify-start gap-3 rounded-none border border-foreground/40 pl-5 pr-1.5 py-1.5 md:pl-6 text-base md:text-lg font-normal text-foreground transition-colors hover:border-foreground"
              >
                {t("sHome.heroSection.ctaSecondary")}
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-none bg-foreground text-background">
                  <PixelArrow />
                </span>
              </a>
            </div>
          </div>

          {/* Reel — desktop only */}
          <button
            type="button"
            onClick={() => setReelOpen(true)}
            aria-label={t("sHome.heroSection.playReelAria")}
            className="hidden lg:block group relative w-[320px] xl:w-[380px] shrink-0 overflow-hidden rounded-none border border-foreground/20"
          >
            <video
              src={reelPreview.url}
              poster={reelPoster.url}
              autoPlay
              muted
              loop
              playsInline
              preload="none"
              className="h-[200px] xl:h-[230px] w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <span className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" aria-hidden />
            <span className="absolute bottom-3 left-4 flex items-center gap-2 text-sm uppercase tracking-widest text-foreground">
              <span className="flex h-7 w-7 items-center justify-center bg-foreground text-background">
                <PixelArrow />
              </span>
              {t("sHome.heroSection.playReel")}
            </span>
          </button>
        </div>
      </div>

      {reelOpen && (
        <div className="fixed inset-0 z-[100] hidden lg:flex items-center justify-center bg-black/90 p-8">
          <button
            type="button"
            onClick={() => setReelOpen(false)}
            aria-label={t("sHome.heroSection.close")}
            className="absolute top-6 right-6 flex h-10 w-10 items-center justify-center bg-foreground text-background"
          >
            <X className="h-5 w-5" />
          </button>
          <video
            src={reelFull.url}
            poster={reelPoster.url}
            controls
            autoPlay
            playsInline
            className="max-h-[80vh] w-full max-w-5xl"
          />
        </div>
      )}
    </section>
  );
};

export default HeroSection;
